import { useState, type ReactNode } from 'react';
import { Color, Size, condensedIsNarrow, reminderSummary, taggedRecordKindWords, type EventReminderDraft, crossesMidnight, fieldOrder, formatPhone, placeField, showsSiteRequirements, t, toggleCondensedRow, withTaggedRecord, zoneNoteNeeded, type Attending, type AvatarPerson, type EventField, type EventType, type StringKey } from '@bms2/shared';
import { Link, useInRouterContext } from 'react-router-dom';
import { useViewerZone } from '../../shell/ViewerZone';
import { Button } from '../controls';
import { Icon } from '../Icon';
import { AvatarRow } from './AttendeeAvatar';
import { useMeasuredWidth } from '../useMeasuredWidth';
import styles from './EventBooking.module.css';

/**
 * Event Booking (piece 4): an Expanded calendar pop-out and a Condensed list
 * row, chosen by available space. Six fields render in a fixed order that
 * branches only on eventType; the Expanded variant renders a Site
 * Requirements band from the resolver it is handed (decision 0134); the
 * Condensed row carries the EVENTS band, its own narrow sub-breakpoint at
 * the 432px token, a Notes preview capped at the two-line token, and a
 * chevron that expands the row in place to read-only fields, editing any of
 * which opens the Event modal (decision 0108).
 */
export const eventBookingDocumentedStates = ['expanded', 'expandedMidnight', 'expandedSiteRequirements', 'condensed', 'condensedOpen', 'condensedNarrow'] as const;

export interface EventAttendee {
  person: AvatarPerson;
  attending: Attending;
}

/** The event as the row shows it; the Event record itself lands with the Calendar. */
export interface EventSummary {
  id: string;
  subject: string;
  eventType: EventType;
  startsAt: Date;
  endsAt: Date | null;
  timeZone: string;
  location?: string | null;
  /** The place's street address on one line; Open in Maps hands this over, never the name (product owner, 2026-09-10). */
  locationAddress?: string | null;
  /** The place's map point by decision 0674's rule, the pin's coordinates where it has one; Open in Maps hands this over first (decision 1600). */
  locationMapsQuery?: string | null;
  meetingLink?: string | null;
  callNumber?: string | null;
  /** The digits a caller types after the bridge answers. */
  callNumberExtension?: string | null;
  attendees: readonly EventAttendee[];
  owner: string;
  notes?: string | null;
  /** What the event is tagged to, which decides the LOCATION field's scope. */
  taggedRecordType?: string | null;
  /** The tagged record by name, "Deal · Line 3 Retrofit", and where it lives; the Expanded card draws it after the attendees (figure 68). */
  taggedRecordLabel?: string | null;
  taggedRecordRoute?: string | null;
}

export interface SiteRequirement {
  label: string;
}

const fieldLabelKey: Record<EventField, StringKey> = {
  subject: 'event.field.subject',
  when: 'event.field.when',
  location: 'event.field.location',
  meetingLink: 'event.field.meetingLink',
  callNumber: 'event.field.callNumber',
  attendees: 'event.field.attendees',
  owner: 'event.field.owner',
  notes: 'event.field.notes',
  taggedRecord: 'event.field.taggedRecord',
};

function formatMoment(d: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(d);
}

function formatTime(d: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', { timeStyle: 'short', timeZone }).format(d);
}

/**
 * 🔨 The row renders in the viewer's own zone and names the event's zone beside
 * it wherever the two differ; it never translates one silently (piece 12).
 * 💰 The row used to render in the event's zone alone, so a nine o'clock
 * meeting three time zones away read as nine o'clock and was missed by three
 * hours (found while building, 2026-09-09).
 */
function whenText(event: EventSummary, viewerZone: string): { start: string; end: string | null } {
  const suffix = zoneNoteNeeded(viewerZone, event.timeZone) ? ` (${formatTime(event.startsAt, event.timeZone)} ${event.timeZone})` : '';
  const start = formatMoment(event.startsAt, viewerZone);
  if (!event.endsAt) return { start: `${start}${suffix}`, end: null };
  if (crossesMidnight(event.startsAt, event.endsAt, viewerZone)) {
    return { start: `${start}${suffix}`, end: `${t('event.ends')} ${formatMoment(event.endsAt, viewerZone)}` };
  }
  return { start: `${start} – ${formatTime(event.endsAt, viewerZone)}${suffix}`, end: null };
}

/** 🔒 The Tagged record line's text: the record by name, else its kind in plain words, never a stored type name or an id (T781). */
function taggedText(event: EventSummary): string {
  return event.taggedRecordLabel ?? (event.taggedRecordType ? taggedRecordKindWords(event.taggedRecordType) : '');
}

function TaggedRecord({ event }: { event: EventSummary }) {
  const inRouter = useInRouterContext();
  const label = taggedText(event);
  if (event.taggedRecordRoute && inRouter) {
    return (
      <Link to={event.taggedRecordRoute} className={styles.taggedRecord} data-event-tagged={event.taggedRecordType ?? ''}>
        {label}
      </Link>
    );
  }
  return (
    <span className={styles.taggedRecord} data-event-tagged={event.taggedRecordType ?? ''}>
      {label}
    </span>
  );
}

function fieldValue(event: EventSummary, field: EventField, viewerZone: string): ReactNode {
  switch (field) {
    case 'taggedRecord':
      return <TaggedRecord event={event} />;
    case 'subject':
      return event.subject;
    case 'when': {
      const w = whenText(event, viewerZone);
      return (
        <>
          <span>{w.start}</span>
          {w.end ? (
            <span className={styles.endsLine} data-ends-line="true">
              {w.end}
            </span>
          ) : null}
        </>
      );
    }
    /**
     * 🔨 The place's name and its street address, one under the other. 💰 The
     * address was carried on the card and drawn nowhere, so LOCATION read as a
     * site's short name — "Tese" — with no way to tell where that is (product
     * owner, 2026-09-14).
     */
    case 'location': {
      const name = event.location ?? '';
      const street = event.locationAddress ?? '';
      if (!street || street === name) return name;
      return (
        <>
          <span>{name}</span>
          <span className={styles.endsLine} data-location-address="true">
            {street}
          </span>
        </>
      );
    }
    case 'meetingLink':
      return event.meetingLink ?? '';
    case 'callNumber':
      return formatPhone(event.callNumber, event.callNumberExtension);
    case 'attendees':
      return <AvatarRow attendees={event.attendees} />;
    case 'owner':
      return event.owner;
    case 'notes':
      return event.notes ?? '';
  }
}

export interface EventBookingExpandedProps {
  event: EventSummary;
  /** The Site Requirements resolver's answer; nothing renders for nothing, or for an Online or Phone event. */
  siteRequirements?: readonly SiteRequirement[] | null;
  /** Absent on a card that is read-only, such as another person's event in Team View (piece 12). */
  onEdit?: (() => void) | undefined;
  /** 🔒 The viewer's own reminder, named under the time where one is set (decision 1081). */
  reminder?: EventReminderDraft | null;
}

/** The enlarged Date / Time of figure 22: "Aug 13 · 10:00 PM", the end on the same line when it is the same day and on its own line with its date otherwise. */
function headlineWhen(event: EventSummary, viewerZone: string): { start: string; sameDayEnd: string | null; nextDayEnd: string | null; zoneNote: string | null } {
  const day = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: viewerZone }).format(event.startsAt);
  const zoneNote = zoneNoteNeeded(viewerZone, event.timeZone) ? `${formatTime(event.startsAt, event.timeZone)} ${event.timeZone}` : null;
  const start = `${day} · ${formatTime(event.startsAt, viewerZone)}`;
  if (!event.endsAt) return { start, sameDayEnd: null, nextDayEnd: null, zoneNote };
  if (crossesMidnight(event.startsAt, event.endsAt, viewerZone)) {
    const endDay = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: viewerZone }).format(event.endsAt);
    return { start, sameDayEnd: null, nextDayEnd: `→ ${endDay}, ${formatTime(event.endsAt, viewerZone)}`, zoneNote };
  }
  return { start, sameDayEnd: formatTime(event.endsAt, viewerZone), nextDayEnd: null, zoneNote };
}

/** The card's one Button, by type: Open in Maps on a Physical event, Join Meeting on an Online one, Call Now on a Phone one (figure 22). */
function typeAction(event: EventSummary): { label: string; color: Color.Navy | Color.Good; href: string | null; kind: 'maps' | 'join' | 'call' } {
  if (event.eventType === 'Online') return { label: t('event.joinMeeting'), color: Color.Good, href: event.meetingLink ?? null, kind: 'join' };
  if (event.eventType === 'Phone') return { label: t('event.callNow'), color: Color.Good, href: event.callNumber ? `tel:${event.callNumber}` : null, kind: 'call' };
  const query = event.locationMapsQuery ?? event.locationAddress ?? event.location ?? null;
  return { label: t('event.openInMaps'), color: Color.Navy, href: query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : null, kind: 'maps' };
}

/**
 * The Expanded card as figure 22 draws it: the type's accent bar on the left,
 * Date / Time first and enlarged with the pencil beside it, then Subject,
 * Notes, Attendees as avatars, the Tagged Record as a Button, the type's own
 * field, and the type's one action Button at the foot. 🔨 The card is
 * read-only; the pencil opens the Event modal and nothing here writes.
 */
export function EventBookingExpanded({ event, siteRequirements, onEdit, reminder = null }: EventBookingExpandedProps) {
  const viewerZone = useViewerZone();
  const fields = withTaggedRecord(fieldOrder(event.eventType), taggedText(event) !== '').filter((field) => field !== 'when');
  const band = showsSiteRequirements(event.eventType, siteRequirements);
  const when = headlineWhen(event, viewerZone);
  const action = typeAction(event);
  const reminderText = reminder ? `${t('event.reminder')}: ${reminderSummary(reminder)}` : null;
  const open = () => {
    if (action.href) window.open(action.href, action.kind === 'call' ? '_self' : '_blank', 'noopener');
  };
  return (
    <article className={styles.expanded} data-event-booking="expanded" data-event-type={event.eventType} aria-label={event.subject}>
      <header className={styles.headline} data-field="when">
        <div className={styles.headlineWhen}>
          <span className={styles.headlineStart}>{when.start}{when.sameDayEnd ? ` – ${when.sameDayEnd}` : ''}</span>
          {when.nextDayEnd ? <span className={styles.endsLine} data-ends-line="true">{when.nextDayEnd}</span> : null}
          {when.zoneNote ? <span className={styles.endsLine} data-zone-note="true">{when.zoneNote}</span> : null}
          {reminder ? <span className={styles.endsLine} data-event-reminder={reminder.channel}>{reminderText}</span> : null}
        </div>
        {onEdit ? (
          <button type="button" className={styles.pencil} onClick={onEdit} aria-label={t('event.edit')} data-event-edit="true">
            <Icon meaning="edit" size="inline" />
          </button>
        ) : null}
      </header>
      <dl className={styles.fields}>
        {fields.map((field) => (
          <div key={field} className={styles.field} data-field={field}>
            <dt>{t(fieldLabelKey[field])}</dt>
            <dd>{fieldValue(event, field, viewerZone)}</dd>
          </div>
        ))}
      </dl>
      {band ? (
        <div className={styles.requirements} data-site-requirements="true">
          <span className={styles.requirementsTitle}>{t('event.siteRequirements')}</span>
          <ul className={styles.requirementsList}>
            {siteRequirements!.map((r) => (
              <li key={r.label}>{r.label}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className={styles.expandedActions} data-event-action={action.kind} data-event-action-href={action.href ?? ''}>
        <Button size={Size.Medium} color={action.color} text={[action.label]} actions={[open]} disabled={!action.href} />
      </div>
    </article>
  );
}

export interface EventBookingCondensedProps {
  event: EventSummary;
  onEdit: () => void;
  initiallyExpanded?: boolean;
  /** A measured width for the suite and the harness; the row measures itself otherwise. */
  widthOverride?: number;
}

/** The three-letter month and the day number the calendar-day icon draws, in the viewer's zone. */
function dayIconParts(startsAt: Date, viewerZone: string): { month: string; day: string } {
  const parts = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: viewerZone }).formatToParts(startsAt);
  return { month: parts.find((p) => p.type === 'month')?.value ?? '', day: parts.find((p) => p.type === 'day')?.value ?? '' };
}

/** Date and time together, "August 13, 10:00 AM", the emphasis Expanded gives it (specification 2.9.2). */
/** The date and time on one line; the zone note, where the viewer's zone differs, is its own caption line beneath rather than a parenthesis that wraps (product owner, 2026-09-10). */
function condensedWhen(event: EventSummary, viewerZone: string): { text: string; zone: string | null } {
  const date = new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', timeZone: viewerZone }).format(event.startsAt);
  const zone = zoneNoteNeeded(viewerZone, event.timeZone) ? `${formatTime(event.startsAt, event.timeZone)} ${event.timeZone}` : null;
  return { text: `${date}, ${formatTime(event.startsAt, viewerZone)}`, zone };
}

interface EventBookingRowProps {
  event: EventSummary;
  onEdit: () => void;
  narrow: boolean;
  initiallyExpanded?: boolean;
}

/**
 * One Condensed row (specification 2.9.2, figures 26 and 27): the accent bar
 * and calendar-day icon on the left, Title over an enlarged Date and Time,
 * the Tagged Record as a Button and the Notes preview when the row is wide,
 * and the chevron on the right that expands the row in place.
 */
function EventBookingRow({ event, onEdit, narrow, initiallyExpanded = false }: EventBookingRowProps) {
  const viewerZone = useViewerZone();
  const [row, setRow] = useState({ expanded: initiallyExpanded });
  const icon = dayIconParts(event.startsAt, viewerZone);
  const place: EventField = placeField(event.eventType);
  const revealed: EventField[] = ['notes', 'attendees', place];
  const showsTag = !narrow && taggedText(event) !== '';
  const showsNotes = !narrow && Boolean(event.notes) && !row.expanded;

  return (
    <div className={styles.condensedRow} data-event-row={event.id} data-event-type={event.eventType} data-expanded={row.expanded ? 'true' : 'false'}>
      <div className={styles.row}>
        <span className={styles.dayIcon} aria-hidden="true" data-event-day-icon="true">
          <span className={styles.dayMonth}>{icon.month}</span>
          <span className={styles.dayNumber}>{icon.day}</span>
        </span>
        {/* 🔨 The date leads and the subject reads beneath it with the place beside it
            (product owner's reference layout, 2026-09-20; decision 0594). A diary is
            scanned by date, and the subject answers "which one" once the date has
            found the day. */}
        <div className={styles.summary}>
          <span className={styles.when} data-event-when="true">{condensedWhen(event, viewerZone).text}</span>
          <span className={styles.subject} data-event-subject="true">{[event.subject, event.location].filter(Boolean).join(' · ')}</span>
          {condensedWhen(event, viewerZone).zone ? <span className={styles.whenZone} data-event-zone="true">{condensedWhen(event, viewerZone).zone}</span> : null}
        </div>
        {showsTag || showsNotes ? (
          <div className={styles.aside} data-event-aside="true">
            {showsTag ? <TaggedRecord event={event} /> : null}
            {showsNotes ? (
              <span className={styles.notesPreview} data-notes-preview="true">
                {event.notes}
              </span>
            ) : null}
          </div>
        ) : null}
        <button
          type="button"
          className={styles.chevron}
          aria-expanded={row.expanded}
          aria-label={t(row.expanded ? 'event.collapse' : 'event.expand')}
          data-event-chevron="true"
          onClick={() => setRow(toggleCondensedRow(row))}
        >
          <Icon meaning={row.expanded ? 'collapse' : 'expand'} size="header" />
        </button>
      </div>
      {/* 🔒 One pencil for the whole opened card, at its top right (decision 1327): each field's own pencil
          opened the same Event window, so three in one column said one thing three times. */}
      {row.expanded ? (
        <div className={styles.revealed} data-event-revealed="true">
          <dl className={styles.revealedFields}>
            {revealed.map((field) => (
              <div key={field} className={styles.field} data-field={field} data-readonly="true">
                <dt>{t(fieldLabelKey[field])}</dt>
                <dd className={styles.revealedValue}>
                  <span className={styles.revealedText}>{fieldValue(event, field, viewerZone)}</span>
                </dd>
              </div>
            ))}
          </dl>
          <button type="button" className={styles.editButton} aria-label={t('event.edit')} onClick={onEdit} data-event-edit="card">
            <Icon meaning="edit" size="inline" />
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * 🔒 How many Condensed rows a record page's Upcoming Events tile stands
 * before the rest scroll inside it (T149, product owner, 2026-09-21: *"should
 * show up to five then offer a scroll bar for size and ease of use"*). The
 * height the box is capped at is `--event-list-max-height` in
 * `styles/tokens.css`, and the two move together.
 */
export const EVENT_LIST_VISIBLE_ROWS = 5;

export interface EventBookingListProps {
  events: readonly EventSummary[];
  onEdit: (event: EventSummary) => void;
  /** The EVENTS band, drawn once above the rows; a record page's tile carries its own header instead. */
  band?: boolean;
  initiallyExpanded?: boolean;
  widthOverride?: number;
}

/** 🔨 A list of Condensed rows draws the band once and measures its width once, so every row breaks narrow together. */
export function EventBookingList({ events, onEdit, band = true, initiallyExpanded = false, widthOverride }: EventBookingListProps) {
  const [ref, width] = useMeasuredWidth<HTMLDivElement>({ override: widthOverride });

  const narrow = width !== null && condensedIsNarrow(width);
  return (
    <div ref={ref} className={styles.condensed} data-event-booking="condensed" data-narrow={narrow ? 'true' : 'false'} data-band={band ? 'true' : 'false'}>
      {band ? <div className={styles.band}>{t('event.band')}</div> : null}
      {events.map((event) => (
        <EventBookingRow key={event.id} event={event} narrow={narrow} initiallyExpanded={initiallyExpanded} onEdit={() => onEdit(event)} />
      ))}
    </div>
  );
}

/** One event as a Condensed list of one: the band and the row, the shape the catalog and the suite know. */
export function EventBookingCondensed({ event, onEdit, initiallyExpanded = false, widthOverride }: EventBookingCondensedProps) {
  return <EventBookingList events={[event]} onEdit={onEdit} initiallyExpanded={initiallyExpanded} {...(widthOverride === undefined ? {} : { widthOverride })} />;
}
